import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useGetIdentity } from '@refinedev/core';
import type { IERPUser } from '@admin-panel/types';
import { isJitAssignmentOnlyUser } from '@admin-panel/pages/orders/packages/jitAssignmentAccess';

const JIT_TAB = 'jit_b2b2c_packages';
const JIT_QUEUE = `/ordercontrolcenter?controlCenterTab=${JIT_TAB}&jitAssignment=pending`;

/** Central dispatch does not require, or grant, a working warehouse selection. */
export const HubFacilityGate = ({
  facilityReady,
  children,
  fallback,
}: {
  facilityReady: boolean;
  children: ReactNode;
  fallback: ReactNode;
}) => {
  const { data: user, isLoading, isError } = useGetIdentity<IERPUser>();
  const { pathname, search } = useLocation();
  if (facilityReady) return <>{children}</>;
  if (isLoading || isError || !isJitAssignmentOnlyUser(user?.roles)) return <>{fallback}</>;

  if (pathname === '/') return <Navigate to={JIT_QUEUE} replace />;
  if (pathname === '/ordercontrolcenter') {
    const params = new URLSearchParams(search);
    const tabs = params.getAll('controlCenterTab');
    if (tabs.length === 0) {
      // Stage's first tab is the Undelivered tower, so an implicit tab must be made explicit.
      params.set('controlCenterTab', JIT_TAB);
      if (!params.has('jitAssignment')) params.set('jitAssignment', 'pending');
      return <Navigate to={{ pathname, search: `?${params.toString()}` }} replace />;
    }
    if (tabs.length === 1 && tabs[0] === JIT_TAB) return <>{children}</>;
  }
  const packagePath = /^\/packages\/show\/([1-9][0-9]*)$/.exec(pathname);
  if (packagePath && Number.isSafeInteger(Number(packagePath[1]))) return <>{children}</>;

  // Package scope/assignment eligibility remains enforced by the focused detail and backend.
  return <>{fallback}</>;
};
